import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Search,
  Build as InstallationIcon,
  CloudSync,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../context/AuthContext';
import { InstallationService } from '../../types';

interface InstallationFilters {
  search?: string;
  is_active?: boolean;
  page?: number;
  limit?: number;
}

export const InstallationList: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Estados
  const [installations, setInstallations] = useState<InstallationService[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false); // Separate loading for search
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50); // 50 instalaciones por página por defecto
  
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Filtros
  const [filters, setFilters] = useState<InstallationFilters>({
    is_active: true,
    page: 1,
    limit: 1000, // Cargar todos los datos y paginar en frontend
  });

  // Estados para sincronización con Google Sheets
  const [syncDialog, setSyncDialog] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    success: boolean;
    stats: { total: number; created: number; updated: number; deactivated: number; errors: number };
    error?: string;
  } | null>(null);

  // Estados para filtros avanzados - eliminado showFilters
  // const [showFilters, setShowFilters] = useState(false);

  // Función para filtrar instalaciones
  const filterInstallations = (installations: InstallationService[], searchTerm: string, isActive?: boolean): InstallationService[] => {
    return installations.filter((installation) => {
      const matchesSearch = !searchTerm || 
        installation.trabajo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (installation.descripcion && installation.descripcion.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (installation.unidad && installation.unidad.toLowerCase().includes(searchTerm.toLowerCase()));
      
      const matchesActive = isActive === undefined || installation.is_active === isActive;
      
      return matchesSearch && matchesActive;
    });
  };

  // Instalaciones filtradas
  const filteredInstallations = filterInstallations(installations, debouncedSearchTerm, filters.is_active);
  
  // Cálculos de paginación
  const totalInstallations = filteredInstallations.length;
  const totalPagesCalculated = Math.ceil(totalInstallations / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInstallations = filteredInstallations.slice(startIndex, endIndex);

  // Reset página cuando cambia la búsqueda o filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filters.is_active, itemsPerPage]);

  // Cargar instalaciones
  const loadInstallations = async (isInitialLoad = false) => {
    try {
      // Only show main loading spinner on initial load
      if (isInitialLoad) {
        setLoading(true);
      } else {
        setSearchLoading(true);
      }
      setError(null);
      
      const response = await apiService.get<InstallationService[]>('/installation-services/', {
        params: {
          limit: filters.limit,
          is_active: filters.is_active
        }
      });

      setInstallations(response || []);
      setTotal(response?.length || 0);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Error al cargar instalaciones');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Efectos
  useEffect(() => {
    loadInstallations(true); // Initial load
  }, []);

  useEffect(() => {
    if (filters.is_active !== undefined) {
      loadInstallations(false); // Filter load
    }
  }, [filters.is_active]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500); // 500ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Handlers
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setCurrentPage(1); // Reset página al buscar
  };

  const handleActiveFilterChange = (event: any) => {
    const value = event.target.value;
    setFilters(prev => ({ 
      ...prev, 
      is_active: value === '' ? undefined : value === 'true'
    }));
    setCurrentPage(1);
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleSyncFromGoogleSheets = async () => {
    try {
      setSyncing(true);
      setSyncResult(null);
      
      // 1. Cargar configuración desde la BD
      const settings = await apiService.get<Array<{ key: string; value: string; category: string }>>('/settings/', {
        params: { category: 'google_sheets' }
      });
      
      const spreadsheetId = settings.find(s => s.key === 'installations_spreadsheet_id')?.value;
      const rangeName = settings.find(s => s.key === 'installations_range_name')?.value;
      const headerRow = parseInt(settings.find(s => s.key === 'installations_header_row')?.value || '0');
      
      if (!spreadsheetId || !rangeName) {
        setError('La configuración de Google Sheets no está completa. Contacta al administrador.');
        setSyncing(false);
        return;
      }
      
      // 2. Validar la estructura de la hoja
      const validation = await apiService.get<{ valid: boolean; error?: string }>('/google-sheets/validate-installation-sheet', {
        params: { spreadsheet_id: spreadsheetId, range_name: rangeName, header_row: headerRow }
      });
      
      if (!validation.valid) {
        setError(`Error de validación: ${validation.error}`);
        setSyncing(false);
        return;
      }
      
      // 3. Mostrar diálogo de confirmación
      setSyncDialog(true);
      setSyncing(false); // Liberar el estado de carga para habilitar el botón del diálogo
      
    } catch (err: any) {
      console.error('Error preparing sync:', err);
      setError(err.response?.data?.detail || 'Error al preparar la sincronización');
      setSyncing(false);
    }
  };

  const confirmSync = async () => {
    try {
      setSyncing(true);
      setSyncDialog(false);
      
      // Cargar configuración nuevamente
      const settings = await apiService.get<Array<{ key: string; value: string; category: string }>>('/settings/', {
        params: { category: 'google_sheets' }
      });
      
      const spreadsheetId = settings.find(s => s.key === 'installations_spreadsheet_id')?.value;
      const rangeName = settings.find(s => s.key === 'installations_range_name')?.value;
      const headerRow = parseInt(settings.find(s => s.key === 'installations_header_row')?.value || '0');
      
      // 4. Ejecutar sincronización
      const result = await apiService.post<any>('/google-sheets/sync-installations', null, {
        params: {
          spreadsheet_id: spreadsheetId,
          range_name: rangeName,
          header_row: headerRow,
          dry_run: false
        }
      });
      
      // Adaptar formato de respuesta del backend al formato esperado
      const formattedResult = {
        success: true,
        stats: {
          total: result.processed || 0,
          created: result.created || 0,
          updated: result.updated || 0,
          deactivated: 0, // El endpoint de instalaciones no retorna deactivated
          errors: Array.isArray(result.errors) ? result.errors.length : 0
        }
      };
      
      setSyncResult(formattedResult);
      
      // 5. Recargar instalaciones
      await loadInstallations(true);
      
    } catch (err: any) {
      console.error('Error syncing installations:', err);
      setSyncResult({
        success: false,
        error: err.response?.data?.detail || 'Error al sincronizar instalaciones',
        stats: { total: 0, created: 0, updated: 0, deactivated: 0, errors: 0 }
      });
    } finally {
      setSyncing(false);
    }
  };

  const handleLimitChange = (event: any) => {
    setItemsPerPage(event.target.value as number);
    setCurrentPage(1);
  };

  const formatPrice = (price?: number, currency: string = 'ARS') => {
    if (price == null) return '-';
    if (currency === 'USD') {
      return `U$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    } else {
      return `AR$ ${price.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
  };

  if (loading && installations.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      {/* Encabezado y filtros en Card */}
      <Card sx={{ mb: 4, borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)' }}>
        <CardContent>
          <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }} gap={3}>
            <Box>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                Servicios de Instalación
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {totalInstallations > 0 ? (
                  <>
                    Mostrando {startIndex + 1}-{Math.min(endIndex, totalInstallations)} de {totalInstallations} servicio{totalInstallations !== 1 ? 's' : ''}
                    {totalPagesCalculated > 1 && ` (Página ${currentPage} de ${totalPagesCalculated})`}
                  </>
                ) : (
                  'No hay servicios de instalación para mostrar'
                )}
                {searchTerm && ` (filtrado por: "${searchTerm}")`}
              </Typography>
            </Box>
            <Box display="flex" gap={2} flexWrap="wrap">
              <Button
                variant="outlined"
                color="primary"
                startIcon={<CloudSync />}
                onClick={handleSyncFromGoogleSheets}
                disabled={syncing}
                sx={{ boxShadow: '0 2px 8px 0 rgba(66,133,244,0.08)', borderRadius: 2 }}
              >
                {syncing ? 'Sincronizando...' : 'Actualizar desde Google Sheets'}
              </Button>
            </Box>
          </Box>
          {/* Filtros */}
          <Box display="flex" gap={2} alignItems="center" flexWrap="wrap" mt={3}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="Buscar por trabajo o descripción..."
              value={searchTerm}
              onChange={handleSearchChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {searchLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      <Search />
                    )}
                  </InputAdornment>
                ),
              }}
              sx={{ flex: 1, minWidth: '220px', background: '#f8fafc', borderRadius: 2 }}
            />
            
            <FormControl sx={{ minWidth: '120px' }} size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                value={filters.is_active === undefined ? '' : filters.is_active.toString()}
                onChange={handleActiveFilterChange}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="true">Activos</MenuItem>
                <MenuItem value="false">Inactivos</MenuItem>
              </Select>
            </FormControl>

            <FormControl sx={{ minWidth: '120px' }} size="small">
              <InputLabel>Por página</InputLabel>
              <Select
                value={itemsPerPage}
                onChange={handleLimitChange}
                label="Por página"
              >
                <MenuItem value={25}>25</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
                <MenuItem value={200}>200</MenuItem>
                <MenuItem value={500}>Todos</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </CardContent>
      </Card>

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Lista de instalaciones */}
      <TableContainer component={Paper} sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: '0 4px 24px 0 rgba(60,72,100,0.10)' }}>
        <Table>
          <TableHead sx={{ backgroundColor: 'rgb(66,154,45)' }}>
            <TableRow>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>TRABAJO</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>DESCRIPCIÓN</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>UNIDAD</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>PRECIO STANDARD</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>PRECIO COMPLEJO</TableCell>
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>ESTADO</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInstallations.map((installation, index) => (
              <TableRow 
                key={installation.id} 
                sx={{ 
                  backgroundColor: index % 2 === 0 ? 'white' : '#f5f5f5',
                  transition: 'background 0.2s',
                  '&:hover': {
                    backgroundColor: '#e0f2fe' // celeste muy claro
                  }
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="medium">
                    {installation.trabajo}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="textSecondary">
                    {installation.descripcion || '-'}
                  </Typography>
                </TableCell>
                <TableCell align="center">{installation.unidad}</TableCell>
                <TableCell align="center">
                  {formatPrice(installation.precio_standard, installation.moneda_standard)}
                </TableCell>
                <TableCell align="center">
                  {formatPrice(installation.precio_complejo, installation.moneda_compleja)}
                </TableCell>
                <TableCell align="center">
                  <Chip
                    label={installation.is_active ? 'Activo' : 'Inactivo'}
                    color={installation.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredInstallations.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <InstallationIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="textSecondary" gutterBottom>
            No hay instalaciones
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm ? 'No se encontraron instalaciones que coincidan con tu búsqueda.' : 'No hay servicios de instalación disponibles.'}
          </Typography>
        </Box>
      )}

      {/* Controles de paginación */}
      {totalPagesCalculated > 1 && (
        <Box display="flex" justifyContent="center" alignItems="center" mt={3} gap={2}>
          <Pagination
            count={totalPagesCalculated}
            page={currentPage}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
          <Typography variant="body2" color="text.secondary">
            {startIndex + 1}-{Math.min(endIndex, totalInstallations)} de {totalInstallations} instalaciones (Página {currentPage} de {totalPagesCalculated})
          </Typography>
        </Box>
      )}

      {/* Dialog de confirmación para sincronizar */}
      <Dialog
        open={syncDialog}
        onClose={() => !syncing && setSyncDialog(false)}
      >
        <DialogTitle>
          Sincronizar servicios de instalación desde Google Sheets
        </DialogTitle>
        <DialogContent>
          <Typography>
            Esta acción sincronizará los servicios de instalación desde la hoja de cálculo de Google configurada.
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            • Los servicios nuevos serán creados
          </Typography>
          <Typography variant="body2">
            • Los servicios existentes serán actualizados
          </Typography>
          <Typography variant="body2">
            • Los servicios no presentes en la hoja serán desactivados
          </Typography>
          <Typography variant="body2" color="warning.main" sx={{ mt: 2, fontWeight: 'bold' }}>
            ℹ️ Esta operación puede tomar unos segundos
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncDialog(false)} disabled={syncing}>
            Cancelar
          </Button>
          <Button onClick={confirmSync} color="primary" variant="contained" disabled={syncing}>
            Sincronizar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de resultado de sincronización */}
      <Dialog
        open={syncResult !== null}
        onClose={() => setSyncResult(null)}
      >
        <DialogTitle>
          {syncResult?.success ? 'Sincronización completada' : 'Error en la sincronización'}
        </DialogTitle>
        <DialogContent>
          {syncResult?.success ? (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                La sincronización se completó exitosamente:
              </Typography>
              <Typography variant="body2">
                • Total procesados: {syncResult.stats.total}
              </Typography>
              <Typography variant="body2" color="success.main">
                • Creados: {syncResult.stats.created}
              </Typography>
              <Typography variant="body2" color="info.main">
                • Actualizados: {syncResult.stats.updated}
              </Typography>
              <Typography variant="body2" color="warning.main">
                • Desactivados: {syncResult.stats.deactivated}
              </Typography>
              {syncResult.stats.errors > 0 && (
                <Typography variant="body2" color="error.main">
                  • Errores: {syncResult.stats.errors}
                </Typography>
              )}
            </>
          ) : (
            <Typography color="error">
              {syncResult?.error || 'Ocurrió un error durante la sincronización'}
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSyncResult(null)} color="primary" variant="contained">
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InstallationList;